import { useUploadFile } from '@/api'
import { FileType, UPLOAD_LIMITS } from '../types/upload-types'
import type { UploadStatus } from '../types/upload-types'

export type ProgressCallback = (fileKey: string, status: UploadStatus) => void

export interface BulkUploadParams {
  files: File[]
  partnerOrganizationId?: string
  documentType: string
  userOrgType: string
  isHeadless?: boolean
  onProgress?: ProgressCallback
}

export interface BulkUploadResult {
  fileName: string
  success: boolean
  error?: string
}

/** Files sent in one request, with the index of its first file in the selection. */
interface UploadRequest {
  start: number
  files: File[]
}

const sizeOf = (request: UploadRequest) =>
  request.files.reduce((sum, file) => sum + file.size, 0)

/**
 * The requests for a selection. A price sheet's files go in one request, so the API can refuse a
 * Catalog without its Discount Schedule (or the reverse) before it stores either. Other files go one
 * per request.
 */
function toRequests(files: File[], documentType: string): UploadRequest[] {
  if (documentType === FileType.PriceSheet) {
    return [{ start: 0, files }]
  }

  return files.map((file, index) => ({ start: index, files: [file] }))
}

/**
 * Groups requests into upload batches, in order. A batch holds up to
 * CONCURRENT_UPLOADS requests and at most MAX_BATCH_SIZE bytes, because the API
 * keeps each upload in memory. A request at the size limit uploads on its own.
 */
function toBatches(requests: UploadRequest[]): UploadRequest[][] {
  const batches: UploadRequest[][] = []
  let current: UploadRequest[] = []
  let currentSize = 0

  requests.forEach((request) => {
    if (
      current.length > 0 &&
      (current.length >= UPLOAD_LIMITS.CONCURRENT_UPLOADS ||
        currentSize + sizeOf(request) > UPLOAD_LIMITS.MAX_BATCH_SIZE)
    ) {
      batches.push(current)
      current = []
      currentSize = 0
    }
    current.push(request)
    currentSize += sizeOf(request)
  })

  if (current.length > 0) {
    batches.push(current)
  }

  return batches
}

/**
 * Hook for uploading multiple files with progress tracking and concurrency control.
 *
 * Handles:
 * - Batch uploads with configurable concurrency
 * - A price sheet's files in one request
 * - Progress tracking for each file
 * - Error handling
 *
 * @example
 * ```tsx
 * const { uploadFiles } = useBulkFileUpload()
 *
 * const results = await uploadFiles({
 *   files: selectedFiles,
 *   partnerOrganizationId: 'abc-123',
 *   documentType: 'SPA',
 *   userOrgType: 'Manufacturer',
 *   onProgress: (fileName, status) => {
 *     console.log(`${fileName}: ${status.status} - ${status.progress}%`)
 *   }
 * })
 * ```
 */
export function useBulkFileUpload() {
  const { mutateAsync: uploadFile } = useUploadFile()

  const uploadFiles = async ({
    files,
    partnerOrganizationId,
    documentType,
    userOrgType,
    isHeadless,
    onProgress
  }: BulkUploadParams): Promise<PromiseSettledResult<BulkUploadResult>[]> => {
    if (!documentType || !userOrgType) {
      throw new Error('Missing required parameters')
    }

    const results: PromiseSettledResult<BulkUploadResult>[] = []

    // Process requests in batches for concurrency control
    for (const batch of toBatches(toRequests(files, documentType))) {
      const batchPromises = batch.map(async (request) => {
        const fileKeys = request.files.map(
          (file, index) => `${request.start + index}-${file.name}`
        )
        const report = (status: Omit<UploadStatus, 'fileName'>) =>
          request.files.forEach((file, index) =>
            onProgress?.(fileKeys[index], { ...status, fileName: file.name })
          )

        report({ status: 'uploading', progress: 0 })

        if (sizeOf(request) > UPLOAD_LIMITS.MAX_BATCH_SIZE) {
          return failed(
            request,
            `A price sheet upload can be at most ${UPLOAD_LIMITS.MAX_BATCH_SIZE / (1024 * 1024)} MB.`,
            report
          )
        }

        let result
        try {
          result = await uploadFile({
            files: request.files,
            partnerOrganizationId,
            documentType,
            userOrgType,
            isHeadless,
            onProgress: (percent) =>
              report({ status: 'uploading', progress: percent })
          })
        } catch (error) {
          result = {
            success: false,
            error: error instanceof Error ? error.message : undefined
          }
        }

        if (!result.success) {
          return failed(request, result.error || 'Upload failed', report)
        }

        report({ status: 'success', progress: 100 })
        return request.files.map((file) => fulfilled(file.name))
      })

      for (const requestResults of await Promise.all(batchPromises)) {
        results.push(...requestResults)
      }
    }

    return results
  }

  return {
    uploadFiles
  }
}

const fulfilled = (
  fileName: string
): PromiseSettledResult<BulkUploadResult> => ({
  status: 'fulfilled',
  value: { fileName, success: true }
})

/** A failed request: every file in it shows the reason. */
function failed(
  request: UploadRequest,
  errorMessage: string,
  report: (status: Omit<UploadStatus, 'fileName'>) => void
): PromiseSettledResult<BulkUploadResult>[] {
  report({ status: 'error', progress: 0, error: errorMessage })
  return request.files.map(() => ({
    status: 'rejected',
    reason: new Error(errorMessage)
  }))
}
